import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Plus, Briefcase, MapPin, Calendar, DollarSign, Star, ChevronRight, User, Camera, Video, Building, CheckCircle2, Circle, ArrowRight, Sparkles, FileText, Users, Bell, Mail, Send, X, Loader2 } from "lucide-react";
import { WORK_TYPES, WORK_TYPE_ALIASES } from "@/lib/profileOptions";
import { NavBar, NavActions } from "@/components/NavBar";
import { Footer } from "@/components/Footer";
import ProfileSettingsDropdown from "@/components/ProfileSettingsDropdown";

function SubscriptionCard({ subscription, token, onCancel, cancellingSubscription }) {
  var [billingHistory, setBillingHistory] = React.useState(null);
  var [loadingHistory, setLoadingHistory] = React.useState(false);
  var [showHistory, setShowHistory] = React.useState(false);

  function fetchBillingHistory() {
    if (billingHistory) { setShowHistory(!showHistory); return; }
    setLoadingHistory(true);
    setShowHistory(true);
    fetch(API + "/subscriptions/billing-history", {
      headers: { Authorization: "Bearer " + token },
    })
      .then(function(res) { return res.ok ? res.json() : null; })
      .then(function(data) { if (data) setBillingHistory(data.payments || []); })
      .catch(function() { setBillingHistory([]); })
      .finally(function() { setLoadingHistory(false); });
  }

  function formatPlanName(planType) {
    if (!planType) return "Subscription";
    return planType.replace(/_/g, " ").replace(/\b\w/g, function(c) { return c.toUpperCase(); }).replace("Combined", "All Access");
  }

  function formatDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
    catch(e) { return iso; }
  }

  var isCancelled = subscription.cancel_at_period_end;
  var periodEnd = subscription.current_period_end;

  return (
    <Card className="bg-white/[0.03] border-white/10 p-5 mb-8" data-testid="subscription-card">
      {/* Plan info header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
        <div className="flex items-start gap-4">
          <div className={"w-10 h-10 rounded-full flex items-center justify-center shrink-0 " + (isCancelled ? "bg-yellow-500/20" : "bg-green-500/20")}>
            <DollarSign className={"w-5 h-5 " + (isCancelled ? "text-yellow-400" : "text-green-400")} />
          </div>
          <div>
            <p className="font-semibold text-white text-lg" data-testid="plan-name">
              {formatPlanName(subscription.plan_type)}
            </p>
            {isCancelled ? (
              <div>
                <Badge className="bg-yellow-500/15 text-yellow-400 border-yellow-500/30 text-xs mb-1">Cancelled</Badge>
                <p className="text-white/50 text-sm">
                  Access until <span className="text-white/70 font-medium">{formatDate(periodEnd)}</span>
                </p>
                <p className="text-white/40 text-xs mt-1">Your free registration will remain active after this date.</p>
              </div>
            ) : (
              <div>
                <Badge className="bg-green-500/15 text-green-400 border-green-500/30 text-xs mb-1">Active</Badge>
                <p className="text-white/50 text-sm">
                  {periodEnd ? (
                    <span>Next renewal: <span className="text-white/70 font-medium">{formatDate(periodEnd)}</span></span>
                  ) : "Active subscription"}
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isCancelled && (
            <Button
              onClick={onCancel}
              disabled={cancellingSubscription}
              variant="outline"
              className="border-white/20 text-white/60 hover:bg-white/10 hover:text-white rounded-sm text-sm"
              data-testid="cancel-subscription-btn"
            >
              {cancellingSubscription ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
              {cancellingSubscription ? "Cancelling..." : "Cancel Subscription"}
            </Button>
          )}
          <Link to="/pricing">
            <Button variant="outline" className="border-white/20 text-white/60 hover:bg-white/10 hover:text-white rounded-sm text-sm">
              {isCancelled ? "Resubscribe" : "Change Plan"}
            </Button>
          </Link>
        </div>
      </div>

      {/* Billing History toggle */}
      <div className="border-t border-white/5 pt-3">
        <button
          onClick={fetchBillingHistory}
          className="text-sm text-white/50 hover:text-white/70 flex items-center gap-2 transition-colors"
          data-testid="toggle-billing-history"
        >
          <DollarSign className="w-3.5 h-3.5" />
          {showHistory ? "Hide" : "View"} Billing History
          <ChevronRight className={"w-3.5 h-3.5 transition-transform " + (showHistory ? "rotate-90" : "")} />
        </button>

        {showHistory && (
          <div className="mt-3" data-testid="billing-history">
            {loadingHistory ? (
              <div className="text-center py-4 text-white/40 text-sm">
                <Loader2 className="w-4 h-4 animate-spin mx-auto mb-1" />
                Loading billing history...
              </div>
            ) : !billingHistory || billingHistory.length === 0 ? (
              <p className="text-white/40 text-sm py-3">No payment history found.</p>
            ) : (
              <div className="space-y-0">
                {billingHistory.map(function(p, i) {
                  return (
                    <div key={i} className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0" data-testid={"payment-" + i}>
                      <div className="flex items-center gap-3">
                        <div className={"w-2 h-2 rounded-full " + (p.status === "paid" ? "bg-green-400" : p.status === "void" || p.status === "uncollectible" ? "bg-red-400" : "bg-white/30")} />
                        <div>
                          <p className="text-sm text-white/70">{p.description || "Payment"}</p>
                          <p className="text-xs text-white/40">{formatDate(p.date)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium">${p.amount.toFixed(2)}</span>
                        {p.invoice_url && (
                          <a
                            href={p.invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-white/40 hover:text-white/60 underline"
                          >
                            Receipt
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

function Dashboard() {
  var navigate = useNavigate();
  var auth = useAuth();
  var user = auth.user;
  var token = auth.token;
  var logout = auth.logout;

  var [jobs, setJobs] = React.useState([]);
  var [applications, setApplications] = React.useState([]);
  var [hires, setHires] = React.useState([]);
  var [mySeconds, setMySeconds] = React.useState([]);
  var [loading, setLoading] = React.useState(true);
  var [stats, setStats] = React.useState({ jobs: 0, applications: 0, openJobsNearby: 0, activeAssistantsNearby: 0 });
  var [showWelcome, setShowWelcome] = React.useState(false);
  var [subscription, setSubscription] = React.useState(null);
  var [cancellingSubscription, setCancellingSubscription] = React.useState(false);

  // Review Request Modal state
  var [showReviewRequest, setShowReviewRequest] = React.useState(false);
  var [rrEmail, setRrEmail] = React.useState("");
  var [rrName, setRrName] = React.useState("");
  var [rrRoles, setRrRoles] = React.useState([]);
  var [rrMessage, setRrMessage] = React.useState("");
  var [rrSubmitting, setRrSubmitting] = React.useState(false);
  var [rrRemaining, setRrRemaining] = React.useState(null);
  var [rrPendingCount, setRrPendingCount] = React.useState(0);
  var [rrSent, setRrSent] = React.useState(false);
  var [rrDraft, setRrDraft] = React.useState(null);

  // Check if profile is under 50% complete - show welcome only then
  React.useEffect(function() {
    if (user) {
      var dismissedKey = "welcome_dismissed_" + user.user_id;
      var hasBeenDismissed = localStorage.getItem(dismissedKey);
      
      if (hasBeenDismissed) return;
      
      // Calculate profile completion
      var completion = calculateProfileCompletion();
      var assistantPct = (completion.assistant && completion.assistant.percentage) || 0;
      var studioPct = (completion.studio && completion.studio.percentage) || 0;
      var maxPct = Math.max(assistantPct, studioPct);
      
      // Only show if profile is under 50% complete
      if (maxPct < 50) {
        setShowWelcome(true);
      }
    }
  }, [user]);

  // Calculate profile completion percentage - SEPARATE for each role
  function calculateProfileCompletion() {
    if (!user) return { assistant: null, studio: null };
    
    var isAssistantRole = hasRole("assistant");
    var isStudioRole = hasRole("studio");
    var ap = user.assistant_profile || {};
    var sp = user.studio_profile || {};
    
    var result = { assistant: null, studio: null };
    
    // Assistant profile completion
    if (isAssistantRole) {
      var assistantSteps = [
        { name: "Add profile photo", completed: !!user.profile_image, priority: 1 },
        { name: "Add bio", completed: !!user.bio && user.bio.length > 10, priority: 2 },
        { name: "Set location", completed: !!(user.location?.city || ap.location_info?.city), priority: 3 },
        { name: "Add portfolio images", completed: (ap.portfolio && ap.portfolio.length > 0) || (user.portfolio_images && user.portfolio_images.length > 0), priority: 4 },
        { name: "Set hourly rate", completed: !!ap.rate_min || !!user.hourly_rate, priority: 5 },
        { name: "Add work types", completed: ap.work_types && ap.work_types.length > 0, priority: 6 },
        { name: "Add experience info", completed: !!ap.years_experience && ap.years_experience > 0, priority: 7 },
        { name: "Add gear", completed: ap.gear_items && ap.gear_items.length > 0, priority: 8 }
      ];
      var assistantCompleted = assistantSteps.filter(function(s) { return s.completed; });
      var assistantIncomplete = assistantSteps.filter(function(s) { return !s.completed; }).sort(function(a, b) { return a.priority - b.priority; });
      result.assistant = {
        percentage: Math.round((assistantCompleted.length / assistantSteps.length) * 100),
        completedSteps: assistantCompleted,
        incompleteSteps: assistantIncomplete,
        totalSteps: assistantSteps.length
      };
    }
    
    // Studio profile completion
    if (isStudioRole) {
      var studioSteps = [
        { name: "Add profile photo", completed: !!user.profile_image, priority: 1 },
        { name: "Add bio", completed: !!user.bio && user.bio.length > 10, priority: 2 },
        { name: "Set location", completed: !!(user.location?.city || sp.location_info?.city), priority: 3 },
        { name: "Set hiring genres", completed: sp.hiring_genres && sp.hiring_genres.length > 0, priority: 4 },
        { name: "Set pay rates", completed: sp.pay_rates && sp.pay_rates.length > 0, priority: 5 },
        { name: "Add shooting styles", completed: sp.shooting_styles && sp.shooting_styles.length > 0, priority: 6 }
      ];
      var studioCompleted = studioSteps.filter(function(s) { return s.completed; });
      var studioIncomplete = studioSteps.filter(function(s) { return !s.completed; }).sort(function(a, b) { return a.priority - b.priority; });
      result.studio = {
        percentage: Math.round((studioCompleted.length / studioSteps.length) * 100),
        completedSteps: studioCompleted,
        incompleteSteps: studioIncomplete,
        totalSteps: studioSteps.length
      };
    }
    
    return result;
  }

  var profileCompletion = calculateProfileCompletion();

  // Helper function to parse date strings without timezone offset
  function parseLocalDate(ds) {
    if (!ds) return null;
    var p = ds.split("T")[0].split("-");
    return new Date(+p[0], +p[1]-1, +p[2]);
  }

  // Filter jobs to only current/future open ones for the dashboard count
  var currentOpenJobs = jobs.filter(function(j) {
    if (j.status !== "open") return false;
    if (j.date) {
      var jobDate = parseLocalDate(j.date);
      var today = new Date(new Date().toDateString());
      if (jobDate < today) return false;
    }
    return true;
  });

  // Helper to check user roles - supports both old (role) and new (roles) format
  function hasRole(roleName) {
    if (!user) return false;
    if (user.roles && Array.isArray(user.roles)) {
      return user.roles.includes(roleName);
    }
    return user.role === roleName;
  }

  var isStudio = hasRole("studio");
  var isAssistant = hasRole("assistant");
  // Check if profile is actively turned on (default to true if field is missing)
  var studioActive = isStudio && user?.studio_profile?.hiring_active !== false;
  var assistantActive = isAssistant && user?.assistant_profile?.find_work_active !== false;
  var hasBothRoles = studioActive && assistantActive;

  // Profile completion card dismiss state
  var [dismissedCards, setDismissedCards] = React.useState(function() {
    try {
      var stored = localStorage.getItem("dismissed_completion_cards_" + (user ? user.user_id : ""));
      return stored ? JSON.parse(stored) : {};
    } catch(e) { return {}; }
  });
  function dismissCompletionCard(type) {
    var next = Object.assign({}, dismissedCards);
    next[type] = true;
    setDismissedCards(next);
    if (user) localStorage.setItem("dismissed_completion_cards_" + user.user_id, JSON.stringify(next));
  }

  React.useEffect(function() {
    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  async function fetchDashboardData() {
    try {
      var headers = token ? { Authorization: "Bearer " + token } : {};

      // Fetch jobs if user has an active studio profile
      if (studioActive) {
        var jobsRes = await fetch(API + "/jobs?studio_id=" + user.user_id, {
          headers: headers
        });
        if (jobsRes.ok) {
          var jobsData = await jobsRes.json();
          setJobs(jobsData.jobs || []);
          setStats(function(prev) { return Object.assign({}, prev, { jobs: jobsData.count || 0 }); });
        }
      }

      // Fetch applications if user has an active assistant profile
      if (assistantActive) {
        var appsRes = await fetch(API + "/applications/my", {
          headers: headers
        });
        if (appsRes.ok) {
          var appsData = await appsRes.json();
          setApplications(appsData.applications || []);
          setStats(function(prev) { return Object.assign({}, prev, { applications: appsData.applications ? appsData.applications.length : 0 }); });
        }
      }

      // Fetch open jobs count for assistants (with geo params if available)
      if (assistantActive) {
        try {
          var jobsUrl = API + "/jobs";
          // Add user's location for geo-filtered results
          var loc = user.location || user.assistant_profile?.location_info;
          if (loc?.coordinates && loc.coordinates.length === 2) {
            var radius = loc.work_radius || user.assistant_profile?.work_radius || 50;
            jobsUrl += "?lat=" + loc.coordinates[1] + "&lng=" + loc.coordinates[0] + "&radius=" + radius;
          }
          var openJobsRes = await fetch(jobsUrl, { headers: headers });
          if (openJobsRes.ok) {
            var openJobsData = await openJobsRes.json();
            setStats(function(prev) { return Object.assign({}, prev, { openJobsNearby: openJobsData.count || 0 }); });
          }
        } catch(e) {}
      }

      // Fetch active assistants count for studios
      if (studioActive) {
        try {
          var assistantsRes = await fetch(API + "/users/count?role=assistant", { headers: headers });
          if (assistantsRes.ok) {
            var assistantsData = await assistantsRes.json();
            setStats(function(prev) { return Object.assign({}, prev, { activeAssistantsNearby: assistantsData.count || 0 }); });
          }
        } catch(e) {}
      }

      // Fetch hires for both roles
      var hiresRes = await fetch(API + "/hiring/my-hires", { headers: headers });
      if (hiresRes.ok) {
        var hiresData = await hiresRes.json();
        setHires(hiresData.hires || []);
      }

      // Fetch unique seconds (people) for studio
      if (studioActive) {
        var secondsRes = await fetch(API + "/jobs/my-seconds", { headers: headers });
        if (secondsRes.ok) {
          var secondsData = await secondsRes.json();
          setMySeconds(secondsData.seconds || []);
        }
      }

      // Fetch subscription status
      try {
        var subRes = await fetch(API + "/subscriptions/status", { headers: headers });
        if (subRes.ok) {
          setSubscription(await subRes.json());
        }
      } catch(e) {}
    } catch (error) {
      console.error("Dashboard fetch error:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }

  async function handleLogout() {
    await logout();
    navigate("/");
  }

  async function handleCancelSubscription() {
    if (!window.confirm("Cancel your subscription? You'll keep access until the end of your current billing period, and your free registration stays active.")) return;
    setCancellingSubscription(true);
    try {
      var res = await fetch(API + "/subscriptions/cancel", {
        method: "POST",
        headers: { Authorization: "Bearer " + token },
      });
      var data = await res.json();
      if (res.ok) {
        toast.success(data.message || "Subscription cancelled");
        setSubscription(function(prev) { return Object.assign({}, prev, { cancel_at_period_end: true, status: "cancelled" }); });
      } else {
        toast.error(data.detail || "Failed to cancel");
      }
    } catch(e) {
      toast.error("Network error");
    }
    setCancellingSubscription(false);
  }


  // Fetch remaining review requests when modal opens
  async function openReviewRequestModal() {
    setShowReviewRequest(true);
    setRrSent(false);
    setRrEmail("");
    setRrName("");
    setRrRoles([]);
    setRrMessage("");
    try {
      var headers = token ? { Authorization: "Bearer " + token } : {};
      var res = await fetch(API + "/reviews/my-requests", { headers: headers });
      if (res.ok) {
        var data = await res.json();
        setRrRemaining(data.remaining_this_month);
        setRrPendingCount((data.requests || []).filter(function(r) { return r.status === "pending"; }).length);
      }
    } catch(e) {}
  }

  async function handleReviewRequestSubmit(e) {
    e.preventDefault();
    if (!rrEmail.trim()) { toast.error("Please enter an email address"); return; }
    if (rrRoles.length === 0) { toast.error("Please select at least one role"); return; }
    setRrSubmitting(true);
    try {
      var headers = { Authorization: "Bearer " + token, "Content-Type": "application/json" };
      var res = await fetch(API + "/reviews/request", {
        method: "POST",
        headers: headers,
        body: JSON.stringify({
          recipient_email: rrEmail.trim(),
          recipient_name: rrName.trim() || null,
          roles_performed: rrRoles,
          message: rrMessage.trim() || null,
          // Sent from the requester's own address, which lands far more often
          // than a message from a platform the recipient does not know.
          delivery: "self",
        }),
      });
      if (res.ok) {
        var data = await res.json();
        setRrRemaining(data.remaining_requests);
        setRrDraft(data);
        setRrSent(true);
      } else {
        var err = await res.json();
        toast.error(err.detail || "Failed to send request");
      }
    } catch(e) {
      toast.error("Failed to send review request");
    } finally {
      setRrSubmitting(false);
    }
  }

  function toggleRrRole(value) {
    setRrRoles(function(prev) {
      return prev.includes(value) ? prev.filter(function(r) { return r !== value; }) : [...prev, value];
    });
  }

  function getJobTypeIcon(type) {
    if (type === "photo") return React.createElement(Camera, { className: "w-4 h-4" });
    if (type === "video") return React.createElement(Video, { className: "w-4 h-4" });
    return React.createElement(Briefcase, { className: "w-4 h-4" });
  }

  function getStatusColor(status) {
    // Job statuses - use neutral/white tones
    if (status === "open") return "bg-white/10 text-white border-white/20";
    if (status === "filled") return "bg-white/10 text-white/70 border-white/20";
    if (status === "pending_confirmation") return "bg-white/10 text-white/70 border-white/20";
    if (status === "completed") return "bg-white/5 text-white/50 border-white/10";
    if (status === "expired") return "bg-amber-500/10 text-amber-400/70 border-amber-500/20";
    if (status === "withdrawn") return "bg-white/5 text-white/30 border-white/10";
    // Application statuses (assistant) - use coral for accepted only
    if (status === "pending") return "bg-white/10 text-white/70 border-white/20";
    if (status === "accepted") return "bg-coral/20 text-coral border-coral/30";
    if (status === "rejected") return "bg-white/10 text-white/50 border-white/20";
    return "bg-white/10 text-white/60 border-white/20";
  }

  function formatDate(dateStr) {
    if (!dateStr) return "TBD";
    var datePart = dateStr.split("T")[0];
    var parts = datePart.split("-");
    var date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  function renderJobsList() {
    // Filter to only current/future open jobs for the dashboard
    var openFutureJobs = currentOpenJobs;
    
    if (openFutureJobs.length === 0) {
      return (
        <Card className="bg-white/[0.03] border-white/5 p-12 text-center" data-testid="no-jobs">
          <Briefcase className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No active jobs</h3>
          <p className="text-white/50 mb-6">Create a job posting to start finding talent</p>
          <Link to="/jobs/create">
            <Button className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold" data-testid="create-first-job-btn">
              <Plus className="w-4 h-4 mr-2" />
              Post a Job
            </Button>
          </Link>
        </Card>
      );
    }

    // Sort jobs by date (most recent first)
    const sortedJobs = [...openFutureJobs].sort((a, b) => {
      const dateA = new Date(a.date || a.created_at || 0);
      const dateB = new Date(b.date || b.created_at || 0);
      return dateB - dateA;
    });

    var jobItems = [];
    for (var i = 0; i < sortedJobs.length && i < 5; i++) {
      var job = sortedJobs[i];
      const matchCount = job.match_count || 0;
      const applicantCount = job.applicant_count || 0;
      
      jobItems.push(
        <Link to={"/jobs/" + job.job_id} key={job.job_id}>
          <Card className="bg-white/[0.03] border-white/5 hover:border-white/20 transition-all p-6 card-hover" data-testid={"job-card-" + job.job_id}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  {getJobTypeIcon(job.job_type)}
                  <h3 className="text-xl font-semibold">{job.title}</h3>
                  <Badge className={getStatusColor(job.status) + " rounded-sm"}>
                    {job.status === "pending_confirmation" ? "Pending Hire" : job.status}
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-4 text-white/50 text-sm">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {formatDate(job.date)}
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {job.location_info?.city ? (job.location_info.city + (job.location_info.state ? ", " + job.location_info.state : "")) : job.location?.city ? job.location.city : "Location TBD"}
                  </span>
                  {(job.hourly_rate || job.flat_rate) && (
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-4 h-4" />
                      {job.hourly_rate ? "$" + job.hourly_rate + "/hr" : "$" + job.flat_rate + " flat"}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right space-y-1">
                <div>
                  <span className="text-xl font-bold text-[#3498DB]">{matchCount}</span>
                  <span className="text-white/50 text-sm ml-1">matches</span>
                </div>
                <div>
                  <span className="text-xl font-bold text-coral">{applicantCount}</span>
                  <span className="text-white/50 text-sm ml-1">applied</span>
                </div>
              </div>
            </div>
          </Card>
        </Link>
      );
    }
    return <div className="space-y-4">{jobItems}</div>;
  }

  function renderApplicationsList() {
    if (applications.length === 0) {
      return (
        <Card className="bg-white/[0.03] border-white/5 p-12 text-center" data-testid="no-applications">
          <Briefcase className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No applications yet</h3>
          <p className="text-white/50 mb-6">Browse available jobs and start applying</p>
          <Link to="/jobs">
            <Button className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold" data-testid="browse-jobs-btn">
              Browse Jobs
            </Button>
          </Link>
        </Card>
      );
    }

    var appItems = [];
    for (var i = 0; i < applications.length; i++) {
      var app = applications[i];
      appItems.push(
        <Link to={"/jobs/" + app.job_id} key={app.application_id}>
          <Card className="bg-white/[0.03] border-white/5 hover:border-white/20 transition-all p-6 card-hover" data-testid={"application-card-" + app.application_id}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-xl font-semibold">
                    {app.job ? app.job.title : "Job"}
                  </h3>
                  <Badge className={getStatusColor(app.status) + " rounded-sm"}>
                    {app.status}
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-4 text-white/50 text-sm">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Applied {formatDate(app.created_at)}
                  </span>
                  {app.job && (app.job.location_info?.city || app.job.location?.city) && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {app.job.location_info?.city ? (app.job.location_info.city + (app.job.location_info.state ? ", " + app.job.location_info.state : "")) : app.job.location.city}
                    </span>
                  )}
                  {app.proposed_rate && (
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-4 h-4" />
                      ${app.proposed_rate}/hr proposed
                    </span>
                  )}
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-white/30" />
            </div>
          </Card>
        </Link>
      );
    }
    return <div className="space-y-4">{appItems}</div>;
  }

  function renderHiresList(role) {
    var filtered = hires.filter(function(h) {
      return role === "studio" ? h.is_studio : !h.is_studio;
    });
    var today = new Date(new Date().toDateString());
    var upcoming = filtered.filter(function(h) {
      var d = parseLocalDate(h.job_date);
      return d && d >= today;
    }).sort(function(a, b) { return (parseLocalDate(a.job_date) || 0) - (parseLocalDate(b.job_date) || 0); });
    var past = filtered.filter(function(h) {
      var d = parseLocalDate(h.job_date);
      return !d || d < today;
    }).sort(function(a, b) { return (parseLocalDate(b.job_date) || 0) - (parseLocalDate(a.job_date) || 0); });
    var all = [...upcoming, ...past].slice(0, 5);

    if (all.length === 0) {
      return (
        <Card className="bg-white/[0.03] border-white/5 p-8 text-center">
          <p className="text-white/50">{role === "studio" ? "No seconds hired yet" : "No jobs accepted yet"}</p>
        </Card>
      );
    }

    return (
      <div className="space-y-3">
        {all.map(function(h) {
          var isPast = h.job_date && parseLocalDate(h.job_date) < new Date(new Date().toDateString());
          var needsReview = isPast && !h.my_review;
          return (
            <Card key={h.hire_id} className="bg-white/[0.03] border-white/5 hover:border-white/20 transition-all p-5 cursor-pointer" data-testid={"hire-card-" + h.hire_id} onClick={function() { navigate("/jobs/" + h.job_id); }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <Avatar className="w-10 h-10 border border-white/10 shrink-0">
                    <AvatarImage src={h.counterparty_image} />
                    <AvatarFallback className="bg-white/[0.05] text-white text-sm">
                      {h.counterparty_name ? h.counterparty_name.charAt(0) : "?"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{h.job_title || "Untitled Job"}</span>
                      <Badge className={(h.status === "confirmed" ? "bg-green-500/20 text-green-400 border-green-500/30" : h.status === "pending_confirmation" ? "bg-amber-500/20 text-amber-400 border-amber-500/30" : "bg-white/10 text-white/60 border-white/20") + " text-xs"}>
                        {h.status === "pending_confirmation" ? "Awaiting Response" : h.status}
                      </Badge>
                      {needsReview && (
                        <Badge className="bg-coral/20 text-coral border-coral/30 text-xs">Review Pending</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-white/40 text-sm mt-1">
                      <span className="truncate">{role === "studio" ? h.counterparty_name : ("by " + h.counterparty_name)}</span>
                      {h.job_date && <span>{formatDate(h.job_date)}</span>}
                      {h.final_rate && <span className="text-white/50">{h.final_rate.startsWith("$") ? h.final_rate : "$" + h.final_rate}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {h.contract && (
                    <Button variant="ghost" size="sm" className="text-white/40 hover:text-white text-xs" data-testid={"view-contract-" + h.hire_id} onClick={function(e) { e.stopPropagation(); navigate("/contracts/" + h.contract.contract_id); }}>
                      <FileText className="w-3.5 h-3.5 mr-1" /> Contract
                    </Button>
                  )}
                  {needsReview && (
                    <Button size="sm" className="bg-coral/20 text-coral hover:bg-coral/30 text-xs" data-testid={"leave-review-" + h.hire_id} onClick={function(e) { e.stopPropagation(); navigate("/jobs/" + h.job_id + "?review=true"); }}>
                      <Star className="w-3.5 h-3.5 mr-1" /> Review
                    </Button>
                  )}
                  <ChevronRight className="w-4 h-4 text-white/20" />
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    );
  }

  function renderMySeconds() {
    if (mySeconds.length === 0) {
      return (
        <Card className="bg-white/[0.03] border-white/5 p-8 text-center">
          <p className="text-white/50">No seconds hired yet. Once you hire someone, they'll appear here.</p>
        </Card>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {mySeconds.map(function(person) {
          return (
            <Link to={"/profile/" + person.user_id} key={person.user_id}>
              <Card className="bg-white/[0.03] border-white/5 hover:border-white/20 transition-all p-5 card-hover" data-testid={"my-second-" + person.user_id}>
                <div className="flex items-center gap-4">
                  <Avatar className="w-12 h-12 border border-white/10">
                    <AvatarImage src={person.profile_image} />
                    <AvatarFallback className="bg-white/[0.05] text-white text-sm">
                      {person.name ? person.name.charAt(0) : "?"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold truncate">{person.name}</h4>
                    <div className="flex items-center gap-3 text-white/40 text-sm mt-1">
                      {person.rating && (
                        <span className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-amber-400" />
                          {person.rating.toFixed(1)}
                        </span>
                      )}
                      <span>{person.hire_count} hire{person.hire_count !== 1 ? "s" : ""}</span>
                    </div>
                    {person.work_types && person.work_types.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {person.work_types.slice(0, 2).map(function(wt) {
                          var label = WORK_TYPES.find(function(w) { return w.value === wt; });
                          var displayLabel = label ? label.label : (WORK_TYPE_ALIASES[wt] || wt);
                          return <span key={wt} className="text-xs bg-white/5 border border-white/10 rounded px-2 py-0.5 text-white/50">{displayLabel}</span>;
                        })}
                      </div>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-white/20 shrink-0" />
                </div>
              </Card>
            </Link>
          );
        })}
      </div>
    );
  }

  // Format roles for display
  function getRolesDisplay() {
    var roleLabels = [];
    if (isStudio) roleLabels.push("Studio");
    if (isAssistant) roleLabels.push("Second Shooter");
    return roleLabels.join(" & ");
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      {/* Welcome Modal for New Users */}
      <Dialog open={showWelcome} onOpenChange={setShowWelcome}>
        <DialogContent className="bg-[#111] border-white/10 text-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-coral" />
              Welcome to GottaSecond!
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-white/70">
              {isAssistant 
                ? "You're all set up! Complete your profile to be matched to open jobs. The more info you provide, the better your chances of finding jobs that match your skills and style."
                : "You're all set up! Complete your profile so assistants can find you and you can start posting jobs."
              }
            </p>
            
            <div className="bg-white/[0.05] rounded-lg p-4 space-y-3">
              <p className="text-sm font-medium text-white/50 uppercase tracking-wider">Next Steps:</p>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-coral/20 flex items-center justify-center text-coral text-sm font-bold">1</div>
                  <span>Complete your profile</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-white/60 text-sm font-bold shrink-0">2</div>
                  <span className="text-white/60">{isStudio ? "Post your first job" : "Get matched to jobs when they are posted. Studios will reach out to you if you are a match. You can get matched to jobs for free."}</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-white/60 text-sm font-bold shrink-0">3</div>
                  <span className="text-white/60">{isStudio ? "Review candidates" : "For more control, subscribe to Pro to see job postings in your area and apply directly."}</span>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                className="flex-1 border-white/20 text-white hover:bg-white/10"
                onClick={function() { 
                  setShowWelcome(false);
                  // Mark as "don't show again" permanently
                  localStorage.setItem("welcome_dismissed_" + user.user_id, "true");
                }}
              >
                Don't show again
              </Button>
              <Link to="/profile/edit" className="flex-1">
                <Button 
                  className="w-full bg-coral hover:bg-coral/90 text-white font-bold"
                  onClick={function() { setShowWelcome(false); }}
                >
                  Complete Profile
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Request Modal */}
      <Dialog open={showReviewRequest} onOpenChange={setShowReviewRequest}>
        <DialogContent className="bg-[#111] border-white/10 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Mail className="w-5 h-5 text-coral" />
              Request a Review
            </DialogTitle>
          </DialogHeader>

          {rrSent ? (
            <div className="text-center py-6">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-green-400" />
              </div>
              <h3 className="text-lg font-bold mb-2">Ready to send</h3>
              <p className="text-white/60 text-sm mb-4">
                Paste this into an email to <span className="text-white font-medium">{rrEmail}</span>.
                Coming from your own address, it is far more likely to be read.
              </p>
              <textarea
                readOnly
                value={rrDraft ? (rrDraft.draft_body || rrDraft.review_url) : ""}
                onFocus={function(e) { e.target.select(); }}
                className="w-full bg-white/[0.05] border border-white/10 rounded-sm text-white text-sm p-3 min-h-[170px] mb-3 text-left"
                data-testid="rr-draft-text"
              />
              <div className="flex gap-2 mb-4">
                <Button
                  className="flex-1 bg-coral hover:bg-coral/90 text-white font-bold"
                  onClick={function() {
                    navigator.clipboard.writeText(rrDraft ? (rrDraft.draft_body || rrDraft.review_url) : "")
                      .then(function() { toast.success("Copied — paste it into your email"); })
                      .catch(function() { toast.error("Could not copy. Select the text and copy it manually."); });
                  }}
                  data-testid="rr-copy-btn"
                >
                  Copy message
                </Button>
                <a
                  className="flex-1"
                  href={rrDraft ? ("mailto:" + encodeURIComponent(rrEmail) +
                    "?subject=" + encodeURIComponent(rrDraft.draft_subject || "Would you leave me a review?") +
                    "&body=" + encodeURIComponent(rrDraft.draft_body || rrDraft.review_url)) : "#"}
                >
                  <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10">
                    Open in email
                  </Button>
                </a>
              </div>
              <p className="text-white/40 text-xs mb-6">
                {rrRemaining !== null ? rrRemaining + " request" + (rrRemaining !== 1 ? "s" : "") + " remaining this month" : ""}
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                  onClick={function() { setShowReviewRequest(false); }}
                  data-testid="rr-close-btn"
                >
                  Close
                </Button>
                <Button
                  className="flex-1 bg-coral hover:bg-coral/90 text-white font-bold"
                  onClick={function() { setRrSent(false); setRrEmail(""); setRrName(""); setRrRoles([]); setRrMessage(""); }}
                  data-testid="rr-send-another-btn"
                >
                  Send Another
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleReviewRequestSubmit} className="space-y-5 mt-2">
              {rrRemaining !== null && (
                <p className="text-white/40 text-xs">
                  {rrRemaining} of {5} requests remaining this month
                  {rrPendingCount >= 3 && (
                    <span className="text-amber-400 ml-1">(max 3 pending reached)</span>
                  )}
                </p>
              )}

              <div>
                <label className="text-sm text-white/60 mb-1.5 block">Their Name *</label>
                <input
                  type="text"
                  value={rrName}
                  onChange={function(e) { setRrName(e.target.value); }}
                  placeholder="e.g., Sarah Johnson"
                  className="w-full bg-white/[0.05] border border-white/10 rounded-sm px-3 py-2 text-white placeholder:text-white/30 focus:outline-none focus:border-white/30 text-sm"
                  required
                  data-testid="rr-name-input"
                />
              </div>

              <div>
                <label className="text-sm text-white/60 mb-1.5 block">Their Email *</label>
                <input
                  type="email"
                  value={rrEmail}
                  onChange={function(e) { setRrEmail(e.target.value); }}
                  placeholder="studio@example.com"
                  className="w-full bg-white/[0.05] border border-white/10 rounded-sm px-3 py-2 text-white placeholder:text-white/30 focus:outline-none focus:border-white/30 text-sm"
                  required
                  data-testid="rr-email-input"
                />
              </div>

              <div>
                <label className="text-sm text-white/60 mb-1.5 block">Roles You Performed *</label>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
                  {WORK_TYPES.map(function(wt) {
                    var selected = rrRoles.includes(wt.value);
                    return (
                      <button
                        key={wt.value}
                        type="button"
                        onClick={function() { toggleRrRole(wt.value); }}
                        className={"text-left text-xs px-3 py-2 rounded-sm border transition-all " + (selected ? "border-coral bg-coral/15 text-coral" : "border-white/10 bg-white/[0.03] text-white/60 hover:border-white/20")}
                        data-testid={"rr-role-" + wt.value}
                      >
                        {wt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="text-sm text-white/60 mb-1.5 block">Personal Message (optional)</label>
                <textarea
                  value={rrMessage}
                  onChange={function(e) { setRrMessage(e.target.value); }}
                  placeholder="Add a personal note to your request..."
                  className="w-full bg-white/[0.05] border border-white/10 rounded-sm px-3 py-2 text-white placeholder:text-white/30 focus:outline-none focus:border-white/30 text-sm min-h-[70px] resize-none"
                  data-testid="rr-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={rrSubmitting || (rrRemaining !== null && rrRemaining <= 0) || rrPendingCount >= 3}
                className="w-full bg-coral hover:bg-coral/90 text-white font-bold"
                data-testid="rr-submit-btn"
              >
                {rrSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Review Request
                  </>
                )}
              </Button>
              {rrRemaining !== null && rrRemaining <= 0 && (
                <p className="text-coral/80 text-xs text-center">
                  You've reached the limit of 5 requests this month. Try again next month.
                </p>
              )}
            </form>
          )}
        </DialogContent>
      </Dialog>

      <NavBar>
        {/* Browse Jobs - only show for users with active assistant profile */}
        {user?.roles?.includes("assistant") && user?.assistant_profile?.find_work_active !== false && (
          <Link to="/jobs" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider" data-testid="nav-browse-jobs">
            Jobs Near You
          </Link>
        )}
        {user?.roles?.includes("admin") && (
          <Link to="/admin" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider" data-testid="nav-admin">
            Admin
          </Link>
        )}
        <NavActions />
      </NavBar>

      <main className="flex-1 pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
            <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16 border-2 border-white/10">
                <AvatarImage src={user ? user.profile_image : ""} />
                <AvatarFallback className="bg-white/[0.05] text-white text-xl">
                  {user && user.name ? user.name.charAt(0).toUpperCase() : "?"}
                </AvatarFallback>
              </Avatar>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
                  Welcome, {user ? user.name : ""}
                </h1>
                <p className="text-white/50 text-lg">{getRolesDisplay()}</p>
              </div>
            </div>
            <div className="flex gap-4">
              <Link to={"/profile/" + (user ? user.user_id : "")}>
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-sm" data-testid="view-profile-btn">
                  <User className="w-4 h-4 mr-2" />
                  View Profile
                </Button>
              </Link>
              {studioActive && (
                <Link to="/jobs/create">
                  <Button className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold uppercase tracking-wide" data-testid="create-job-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Post a Job
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Email Verification Banner */}
          {user && !user.email_verified && (
            <Card className="bg-amber-500/10 border-amber-500/30 p-4 mb-8" data-testid="email-verification-banner">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-amber-400 shrink-0" />
                  <div>
                    <p className="font-semibold text-amber-200">Verify your email to unlock all features</p>
                    <p className="text-white/50 text-sm">Check your inbox for a verification link. Some actions are restricted until verified.</p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-amber-500/40 text-amber-300 hover:bg-amber-500/20 shrink-0"
                  data-testid="resend-verification-btn"
                  onClick={function() {
                    fetch(API + "/auth/resend-verification", {
                      method: "POST",
                      headers: { Authorization: "Bearer " + token },
                    })
                      .then(function(r) { return r.json(); })
                      .then(function(data) {
                        var _toast = require("sonner").toast;
                        _toast.success(data.message || "Verification email sent!");
                      })
                      .catch(function() {
                        var _toast = require("sonner").toast;
                        _toast.error("Failed to resend verification email");
                      });
                  }}
                >
                  <Send className="w-3.5 h-3.5 mr-1.5" />
                  Resend Email
                </Button>
              </div>
            </Card>
          )}

          {/* Subscription Management Card */}
          {subscription && subscription.has_subscription && (
            <SubscriptionCard
              subscription={subscription}
              token={token}
              onCancel={handleCancelSubscription}
              cancellingSubscription={cancellingSubscription}
            />
          )}

          {/* Pending Job Offer Notification */}
          {hires.filter(function(h) { return h.status === "pending_confirmation" && !h.is_studio; }).map(function(h) {
            return (
              <div key={h.hire_id} className="mb-8 relative overflow-hidden rounded-lg border border-coral/40 bg-coral/10 backdrop-blur-sm" data-testid={"pending-offer-banner-" + h.hire_id}>
                <div className="absolute inset-0 bg-gradient-to-r from-coral/5 to-transparent pointer-events-none" />
                <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-coral/20 flex items-center justify-center shrink-0 mt-0.5">
                      <Bell className="w-5 h-5 text-coral animate-pulse" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-coral">New Job Offer</h3>
                      <p className="text-white/70 text-sm mt-0.5">
                        <span className="font-medium text-white">{h.counterparty_name}</span> wants to hire you for <Link to={"/jobs/" + h.job_id} className="font-medium text-white underline underline-offset-2 hover:text-coral">{h.job_title}</Link>
                        {h.job_date && <span className="text-white/50"> on {formatDate(h.job_date)}</span>}
                        {h.final_rate && <span className="text-white/50"> &middot; {h.final_rate.startsWith("$") ? h.final_rate : "$" + h.final_rate}{h.rate_type === "hourly" ? "/hr" : h.rate_type === "flat" ? " flat" : ""}</span>}
                      </p>
                    </div>
                  </div>
                  <Link to={"/hiring/confirm/" + h.hire_id} className="shrink-0">
                    <Button className="bg-coral hover:bg-coral/90 text-white font-bold rounded-sm" data-testid={"view-offer-btn-" + h.hire_id}>
                      View &amp; Respond
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })}

          {/* Pending Hire Notifications for Studios */}
          {hires.filter(function(h) { return h.status === "pending_confirmation" && h.is_studio; }).map(function(h) {
            return (
              <div key={h.hire_id} className="mb-8 relative overflow-hidden rounded-lg border border-[#3498DB]/40 bg-[#3498DB]/10 backdrop-blur-sm" data-testid={"pending-studio-banner-" + h.hire_id}>
                <div className="absolute inset-0 bg-gradient-to-r from-[#3498DB]/5 to-transparent pointer-events-none" />
                <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#3498DB]/20 flex items-center justify-center shrink-0 mt-0.5">
                      <Bell className="w-5 h-5 text-[#3498DB]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-[#3498DB]">Hire Pending Response</h3>
                      <p className="text-white/70 text-sm mt-0.5">
                        Waiting on <span className="font-medium text-white">{h.counterparty_name}</span> to respond to your offer for <Link to={"/jobs/" + h.job_id} className="font-medium text-white underline underline-offset-2 hover:text-[#3498DB]">{h.job_title}</Link>
                      </p>
                    </div>
                  </div>
                  <Link to={"/jobs/" + h.job_id} className="shrink-0">
                    <Button variant="outline" className="border-[#3498DB]/40 text-[#3498DB] hover:bg-[#3498DB]/10 font-bold rounded-sm" data-testid={"view-pending-hire-btn-" + h.hire_id}>
                      View Job
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })}

          {/* Stats - Role-specific */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-testid="stats-grid">
            {studioActive && (
              <Card className="bg-white/[0.03] border-white/5 p-6" data-testid="stat-jobs">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/50 text-sm uppercase tracking-wider mb-1">Your Job Listings</p>
                    <p className="text-4xl font-bold">{currentOpenJobs.length}</p>
                  </div>
                  <Building className="w-10 h-10 text-white/20" />
                </div>
              </Card>
            )}
            {studioActive && (
              <Card className="bg-white/[0.03] border-white/5 p-6" data-testid="stat-active-seconds">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/50 text-sm uppercase tracking-wider mb-1">Active Seconds</p>
                    <p className="text-4xl font-bold">{stats.activeAssistantsNearby}</p>
                  </div>
                  <Users className="w-10 h-10 text-white/20" />
                </div>
              </Card>
            )}
            {assistantActive && (
              <Link to="/jobs" data-testid="stat-open-jobs">
                <Card className="bg-white/[0.03] border-white/5 p-6 hover:border-white/20 transition-all cursor-pointer h-full">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/50 text-sm uppercase tracking-wider mb-1">Open Jobs</p>
                      <p className="text-4xl font-bold">{stats.openJobsNearby}</p>
                      <p className="text-white/30 text-xs mt-1">Click to browse open jobs</p>
                    </div>
                    <Briefcase className="w-10 h-10 text-white/20" />
                  </div>
                </Card>
              </Link>
            )}
            {assistantActive && (
              <Card className="bg-white/[0.03] border-white/5 p-6" data-testid="stat-applications">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/50 text-sm uppercase tracking-wider mb-1">Pending Applications</p>
                    <p className="text-4xl font-bold">{stats.applications}</p>
                  </div>
                  <FileText className="w-10 h-10 text-white/20" />
                </div>
              </Card>
            )}
            <div data-testid="stat-rating">
              <Link to="/my-reviews">
                <Card className="bg-white/[0.03] border-white/5 p-6 hover:border-white/20 transition-all cursor-pointer">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/50 text-sm uppercase tracking-wider mb-1">Rating &amp; Reviews</p>
                      <p className="text-4xl font-bold">
                        {user && user.rating ? user.rating.toFixed(1) : "—"}
                        <span className="text-base font-normal text-white/40 ml-2">({user ? (user.review_count || 0) : 0})</span>
                      </p>
                    </div>
                    <Star className="w-10 h-10 text-white/20" />
                  </div>
                </Card>
              </Link>
              {isAssistant && (
                <button
                  onClick={openReviewRequestModal}
                  className="mt-2 w-full flex items-center justify-center gap-2 text-sm text-coral hover:text-coral/80 transition-colors py-1"
                  data-testid="request-reviews-btn"
                >
                  <Mail className="w-3.5 h-3.5" />
                  Request Reviews
                </button>
              )}
            </div>
          </div>

          {/* Profile Completion Cards - Only for active profiles, dismissible, auto-hide at 90%+ */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {/* Assistant Profile Completion */}
            {assistantActive && profileCompletion.assistant && profileCompletion.assistant.percentage < 90 && !dismissedCards.assistant && (
              <Card className="bg-gradient-to-r from-coral/10 to-transparent border-coral/20 p-6 relative" data-testid="assistant-completion-card">
                <button onClick={function() { dismissCompletionCard("assistant"); }} className="absolute top-3 right-3 text-white/30 hover:text-white/70 transition-colors" data-testid="dismiss-assistant-completion" aria-label="Dismiss">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                </button>
                <div className="flex flex-col gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <Camera className="w-5 h-5 text-coral" />
                      <h3 className="text-lg font-bold">Second Shooter Profile</h3>
                      <Badge className="bg-coral/20 text-coral border-coral/30 rounded-full px-3">
                        {profileCompletion.assistant.percentage}%
                      </Badge>
                    </div>
                    <Progress value={profileCompletion.assistant.percentage} className="h-2 mb-3 bg-white/10" />
                    
                    {profileCompletion.assistant.incompleteSteps.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        <span className="text-white/50 text-sm">Next:</span>
                        {profileCompletion.assistant.incompleteSteps.slice(0, 2).map(function(step, i) {
                          return (
                            <span key={i} className="text-sm flex items-center gap-1 text-white/70">
                              <Circle className="w-3 h-3 text-coral" />
                              {step.name}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <Link to="/profile/edit">
                    <Button className="w-full bg-coral hover:bg-coral/90 text-white font-bold">
                      Complete Profile
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </Card>
            )}
            
            {/* Studio Profile Completion */}
            {studioActive && profileCompletion.studio && profileCompletion.studio.percentage < 90 && !dismissedCards.studio && (
              <Card className="bg-gradient-to-r from-[#3498DB]/10 to-transparent border-[#3498DB]/20 p-6 relative" data-testid="studio-completion-card">
                <button onClick={function() { dismissCompletionCard("studio"); }} className="absolute top-3 right-3 text-white/30 hover:text-white/70 transition-colors" data-testid="dismiss-studio-completion" aria-label="Dismiss">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                </button>
                <div className="flex flex-col gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <Building className="w-5 h-5 text-[#3498DB]" />
                      <h3 className="text-lg font-bold">Studio Profile</h3>
                      <Badge className="bg-[#3498DB]/20 text-[#3498DB] border-[#3498DB]/30 rounded-full px-3">
                        {profileCompletion.studio.percentage}%
                      </Badge>
                    </div>
                    <Progress value={profileCompletion.studio.percentage} className="h-2 mb-3 bg-white/10" />
                    
                    {profileCompletion.studio.incompleteSteps.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        <span className="text-white/50 text-sm">Next:</span>
                        {profileCompletion.studio.incompleteSteps.slice(0, 2).map(function(step, i) {
                          return (
                            <span key={i} className="text-sm flex items-center gap-1 text-white/70">
                              <Circle className="w-3 h-3 text-[#3498DB]" />
                              {step.name}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <Link to="/profile/edit/studio">
                    <Button className="w-full bg-[#3498DB] hover:bg-[#2980B9] text-white font-bold">
                      Complete Profile
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </Card>
            )}
          </div>

          {/* Tabbed content for users with both active roles */}
          {hasBothRoles ? (
            <Tabs defaultValue="studio" className="w-full">
              <TabsList className="bg-white/[0.03] border border-white/10 mb-6">
                <TabsTrigger value="studio" className="data-[state=active]:bg-white data-[state=active]:text-black">
                  <Building className="w-4 h-4 mr-2" />
                  Studio Dashboard
                </TabsTrigger>
                <TabsTrigger value="assistant" className="data-[state=active]:bg-white data-[state=active]:text-black">
                  <Camera className="w-4 h-4 mr-2" />
                  Second Shooter
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="studio">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">Your Job Listings</h2>
                  <Link to="/jobs/my-jobs" className="text-white/60 hover:text-white text-sm flex items-center gap-1">
                    View all <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
                {renderJobsList()}
                
                {mySeconds.length > 0 && (
                  <div className="mt-12">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-bold flex items-center gap-2">
                        <Users className="w-6 h-6 text-white/40" />
                        My Seconds
                      </h2>
                    </div>
                    {renderMySeconds()}
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="assistant">
                {hires.some(function(h) { return !h.is_studio; }) && (
                  <div className="mb-12">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-bold flex items-center gap-2">
                        <Briefcase className="w-6 h-6 text-white/40" />
                        My Jobs
                      </h2>
                    </div>
                    {renderHiresList("assistant")}
                  </div>
                )}

                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">Your Applications</h2>
                  <Link to="/jobs" className="text-white/60 hover:text-white text-sm flex items-center gap-1">
                    Browse Jobs <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
                {renderApplicationsList()}
                
                <div className="mt-12">
                  <h2 className="text-2xl font-bold mb-6">Quick Actions</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Link to="/profile/edit">
                      <Card className="bg-white/[0.03] border-white/5 hover:border-white/20 transition-all p-6 card-hover">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-white/5 rounded-sm flex items-center justify-center">
                            <Camera className="w-6 h-6 text-white/60" />
                          </div>
                          <div>
                            <h3 className="font-semibold">Update Your Portfolio</h3>
                            <p className="text-white/50 text-sm">Add your latest work samples</p>
                          </div>
                        </div>
                      </Card>
                    </Link>
                    <Link to="/jobs">
                      <Card className="bg-white/[0.03] border-white/5 hover:border-white/20 transition-all p-6 card-hover">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-white/5 rounded-sm flex items-center justify-center">
                            <Briefcase className="w-6 h-6 text-white/60" />
                          </div>
                          <div>
                            <h3 className="font-semibold">Find Jobs Near You</h3>
                            <p className="text-white/50 text-sm">Search by location and skills</p>
                          </div>
                        </div>
                      </Card>
                    </Link>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          ) : studioActive ? (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">Your Jobs</h2>
                <Link to="/jobs/my-jobs" className="text-white/60 hover:text-white text-sm flex items-center gap-1" data-testid="view-all-jobs">
                  View all <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              {renderJobsList()}
              
              {mySeconds.length > 0 && (
                <div className="mt-12">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                      <Users className="w-6 h-6 text-white/40" />
                      My Seconds
                    </h2>
                  </div>
                  {renderMySeconds()}
                </div>
              )}
            </div>
          ) : (
            <div>
              {hires.some(function(h) { return !h.is_studio; }) && (
                <div className="mb-12">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                      <Briefcase className="w-6 h-6 text-white/40" />
                      My Jobs
                    </h2>
                  </div>
                  {renderHiresList("assistant")}
                </div>
              )}

              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">Your Applications</h2>
                <Link to="/jobs" className="text-white/60 hover:text-white text-sm flex items-center gap-1" data-testid="browse-jobs-link">
                  Browse Jobs <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              {renderApplicationsList()}

              <div className="mt-12">
                <h2 className="text-2xl font-bold mb-6">Quick Actions</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Link to="/profile/edit">
                    <Card className="bg-white/[0.03] border-white/5 hover:border-white/20 transition-all p-6 card-hover" data-testid="update-portfolio-card">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-white/5 rounded-sm flex items-center justify-center">
                          <Camera className="w-6 h-6 text-white/60" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Update Your Portfolio</h3>
                          <p className="text-white/50 text-sm">Add your latest work samples</p>
                        </div>
                      </div>
                    </Card>
                  </Link>
                  <Link to="/jobs">
                    <Card className="bg-white/[0.03] border-white/5 hover:border-white/20 transition-all p-6 card-hover" data-testid="find-jobs-card">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-white/5 rounded-sm flex items-center justify-center">
                          <Briefcase className="w-6 h-6 text-white/60" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Find Jobs Near You</h3>
                          <p className="text-white/50 text-sm">Search by location and skills</p>
                        </div>
                      </div>
                    </Card>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default Dashboard;
